import React, { useContext, useEffect } from 'react';
import { Block } from '../../../../../../types/shared/blocks';
import {
	Box,
	Stack,
	TextField,
	Typography,
	FormControlLabel,
	Radio,
	RadioGroup,
	styled,
} from '@mui/material';
import FeetInchesInput from '../FeetInchesInputV2';
import { DeckContext } from '../../context/DeckContext';
import getParentBlock from '../../../../../../utils/getParentBlock';
import { toastError } from '../../../../../../Toaster/Toast';
import EdgeType from '../../../../../../enums/edgeType';
import {
	MAX_STAIR_HEIGHT,
	MIN_STAIR_WIDTH,
} from '../../../../../../constants/limits';
import EdgeLabel from '../../../../../../enums/edgeLabel';
import {
	DEPTH_COEFFICIENT,
	STEPS_COEFFICIENT,
} from '../../../../../../constants/coefficients';
import { useQuoteState } from 'src/stateProviders/QuoteStateProvider';

const StyledLabel = styled(Typography)({
	fontWeight: 500,
	fontSize: 14,
});

const StairPopper: React.FC<{
	block: Block;
	blockData: Block;
	setBlockData: React.Dispatch<React.SetStateAction<Block>>;
}> = ({ block, blockData, setBlockData }) => {
	const { state } = useContext(DeckContext);
	const quoteState = useQuoteState();

	const { parentBlock } = getParentBlock(state.blocks, block.parentBlock);

	const blockAttachedEdge = block.edges.find(
		(ed) => ed.type == EdgeType.BLOCK_ATTACHED
	);

	const displayWidthDimension =
		blockAttachedEdge?.label == 'Top' || blockAttachedEdge?.label == 'Bottom'
			? 'width'
			: 'depth';

	useEffect(() => {
		setBlockData((prev) => ({
			...prev,
			dimensions: {
				...prev.dimensions,
				endHeight: (prev.dimensions.startHeight || 0) - prev.dimensions.height,
			},
			displayDimensions: {
				width:
					displayWidthDimension == 'width'
						? prev.dimensions.width
						: blockData.dimensions.depth,
				height:
					displayWidthDimension == 'width'
						? blockData.dimensions.depth
						: prev.dimensions.width,
			},
			edges: blockData.edges.map((edge) => {
				if (blockData.dimensions.height > 0) {
					return edge.type == EdgeType.GROUND
						? { ...edge, type: EdgeType.RAILING }
						: edge;
				}
				return edge;
			}),
		}));
	}, [
		blockData.dimensions.height,
		blockData.dimensions.depth,
		blockData.dimensions.width,
	]);

	const handleWidthChange = (inches) => {
		if (!inches) return;

		if (inches < MIN_STAIR_WIDTH) {
			toastError({
				title: 'Width',
				description: `${block.label} width cannot be less than ${MIN_STAIR_WIDTH / 12} ft`,
			});

			inches = MIN_STAIR_WIDTH;
		}

		setBlockData({
			...blockData,
			dimensions: {
				...blockData.dimensions,
				width: inches,
			},
			displayDimensions: {
				width:
					displayWidthDimension == 'width'
						? inches
						: blockData.dimensions.depth,
				height:
					displayWidthDimension == 'width'
						? blockData.dimensions.depth
						: inches,
			},
			edges: blockData.edges.map((edge) => {
				if (displayWidthDimension == 'width') {
					if (edge.label == EdgeLabel.TOP || edge.label == EdgeLabel.BOTTOM) {
						return {
							...edge,
							length: inches,
						};
					}
					return edge;
				} else {
					if (edge.label == EdgeLabel.LEFT || edge.label == EdgeLabel.RIGHT) {
						return {
							...edge,
							length: inches,
						};
					}

					return edge;
				}
			}),
		});
	};

	const handleDepthChange = (inches) => {
		if (!inches || inches == blockData.dimensions.depth) {
			console.log('ignore');
			return;
		}

		const numberOfSteps = Math.ceil(inches / DEPTH_COEFFICIENT);
		const height = Math.ceil(numberOfSteps * STEPS_COEFFICIENT);

		setBlockData({
			...blockData,
			dimensions: {
				...blockData.dimensions,
				numberOfSteps,
				height,
				depth: inches,
			},
		});
	};

	const handleHeightChange = (inches) => {
		if (!inches || inches == blockData.dimensions.height) {
			console.log('ignore');
			return;
		}

		const numberOfSteps = Math.ceil(inches / STEPS_COEFFICIENT);
		const depth = Math.ceil(numberOfSteps * DEPTH_COEFFICIENT);

		setBlockData({
			...blockData,
			dimensions: {
				...blockData.dimensions,
				height: inches,
				numberOfSteps,
				depth,
			},
		});
	};

	const handleNumberOfStepsChange = (e) => {
		let num;
		try {
			num =
				e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0);
		} catch (error) {
			console.log(error);
			num = 0;
		}

		if (num == blockData.dimensions.numberOfSteps) {
			console.log('ignore');

			return;
		}

		const maxSteps = Math.min(
			Math.ceil(parentBlock.dimensions.height / STEPS_COEFFICIENT),
			Math.ceil(MAX_STAIR_HEIGHT / STEPS_COEFFICIENT)
		);

		if (num > maxSteps) {
			toastError({
				title: 'Steps',
				description: `${block.label} number of steps cannot be higher than ${maxSteps}`,
			});
			num = maxSteps;
		}

		const height = Math.min(
			Math.ceil(num * STEPS_COEFFICIENT),
			MAX_STAIR_HEIGHT
		);
		const depth = Math.ceil(num * DEPTH_COEFFICIENT);

		setBlockData({
			...blockData,
			dimensions: {
				...blockData.dimensions,
				numberOfSteps: num,
				height,
				depth,
			},
		});
	};

	return (
		<Stack spacing={1.25}>
			{quoteState.reface && (
				<RadioGroup
					value={blockData.reface ? 'Reface' : 'Rebuild'}
					onChange={(e) => {
						setBlockData((prev) => ({
							...prev,
							reface: e.target.value == 'Reface',
						}));
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'start',
						}}
					>
						<FormControlLabel
							value={'Reface'}
							control={<Radio />}
							label={<StyledLabel>Reface</StyledLabel>}
						/>
						<FormControlLabel
							value={'Rebuild'}
							control={<Radio />}
							label={<StyledLabel>Rebuild</StyledLabel>}
						/>
					</Box>
				</RadioGroup>
			)}

			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Typography sx={{ flexShrink: 0, fontSize: 14, fontWeight: 500 }}>
					Steps
				</Typography>

				<TextField
					value={blockData.dimensions.numberOfSteps || ''}
					variant="outlined"
					size="small"
					onChange={handleNumberOfStepsChange}
					type="number"
					sx={{ flex: 1, ml: 1 }}
					slotProps={{
						htmlInput: {
							min: 0,
							max: Math.ceil(MAX_STAIR_HEIGHT / STEPS_COEFFICIENT),
						},
					}}
				/>
			</Box>

			<FeetInchesInput
				valueInches={blockData.dimensions.depth || 0}
				labelInches="Run Length"
				onChange={handleDepthChange}
				locked={true}
			/>

			<FeetInchesInput
				valueInches={blockData.dimensions.width}
				labelInches="Width"
				onChange={handleWidthChange}
			/>

			<FeetInchesInput
				valueInches={blockData.dimensions.height}
				labelInches="Height"
				onChange={handleHeightChange}
				locked={true}
			/>
		</Stack>
	);
};

export default StairPopper;
